import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import styled from 'styled-components';
import axios from 'axios';
import { FaSun, FaMoon } from 'react-icons/fa';
import { getSportsbooks, updateUserSportsbook, updateEmailSubscription, updateEmailResultsSubscription } from '../services/api';
import SportsbookDropdown from '../components/Predictions/SportsbookDropdown';
import { FaBell, FaBellSlash } from 'react-icons/fa';

const Container = styled.div`
  max-width: 500px;
  margin: 1.5rem auto;
  padding: 1.5rem;
  background: ${props => props.theme.cardBg};
  border-radius: 0.5rem;
  box-shadow: ${props => props.theme.shadow};
  transition: all 0.3s ease;

  @media (max-width: 600px) {
    margin: 0.75rem auto 1.25rem;
    padding: 1.1rem 1.1rem 1.4rem;
  }
`;
const Title = styled.h2`
  margin-bottom: 1.1rem;
  color: ${props => props.theme.primaryText};
  transition: color 0.3s ease;
`;
const Field = styled.div`
  margin-bottom: 0.9rem;
`;
const Label = styled.label`
  display: block;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.primaryText};
  transition: color 0.3s ease;
`;
const Input = styled.input`
  width: 100%;
  padding: 0.65rem;
  border: 1px solid ${props => props.theme.inputBorder};
  border-radius: 0.25rem;
  font-size: 1rem;
  background-color: ${props => props.theme.inputBg};
  color: ${props => props.theme.primaryText};
  transition: all 0.3s ease;
`;
const Button = styled.button`
  padding: 0.6rem 1.25rem;
  background: #4299e1;
  color: white;
  border: none;
  border-radius: 0.25rem;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  margin-top: 0.75rem;
  &:hover { background: #3182ce; }
`;
const Success = styled.div`
  color: ${props => props.theme.success};
  margin-top: 1rem;
  transition: color 0.3s ease;
`;
const Error = styled.div`
  color: ${props => props.theme.error};
  margin-top: 1rem;
  transition: color 0.3s ease;
`;
const PasswordSection = styled.div`
  margin-top: 1.75rem;
  padding-top: 1.4rem;
  border-top: 1px solid ${props => props.theme.border};
  transition: border-color 0.3s ease;
`;

const SportsbookSection = styled.div`
  margin-top: 1.75rem;
  padding-top: 1.4rem;
  border-top: 1px solid ${props => props.theme.border};
  transition: border-color 0.3s ease;
`;

const SportsbookRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: 0.5rem;
`;

const NotificationsSection = styled.div`
  margin-top: 1.75rem;
  padding-top: 1.4rem;
  border-top: 1px solid ${props => props.theme.border};
  transition: border-color 0.3s ease;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.5rem;
`;

const ToggleSwitch = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  width: 44px;
  height: 24px;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  background: ${props => props.$active ? props.theme.success : props.theme.tertiaryBg};
  transition: background 0.2s ease;
  padding: 0;
  flex-shrink: 0;
  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => props.$active ? '22px' : '2px'};
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: white;
    transition: left 0.2s ease;
  }
`;

const ToggleLabel = styled.span`
  font-size: 0.95rem;
  color: ${props => props.theme.primaryText};
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const ProfilePage = () => {
  const { theme } = useTheme();
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [pwForm, setPwForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: ''
  });
  const [pwLoading, setPwLoading] = useState(false);
  const [pwSuccess, setPwSuccess] = useState('');
  const [pwError, setPwError] = useState('');
  const [sportsbooks, setSportsbooks] = useState([]);
  const [sbLoading, setSbLoading] = useState(false);
  const [sbSuccess, setSbSuccess] = useState('');
  const [sbError, setSbError] = useState('');
  const [emailSubscribed, setEmailSubscribed] = useState(user?.emailSubscribed ?? true);
  const [emailResultsSubscribed, setEmailResultsSubscribed] = useState(user?.emailResultsSubscribed ?? true);
  const [subLoading, setSubLoading] = useState(false);
  const [subSuccess, setSubSuccess] = useState('');
  const [subError, setSubError] = useState('');
  const [resSubLoading, setResSubLoading] = useState(false);
  const [resSubSuccess, setResSubSuccess] = useState('');
  const [resSubError, setResSubError] = useState('');

  const preferredSportsbook = user?.preferredSportsbook || 'best';

  useEffect(() => {
    getSportsbooks()
      .then((res) => setSportsbooks(res.sportsbooks || []))
      .catch(() => setSportsbooks([]));
  }, []);

  useEffect(() => {
    if (window.location.hash !== '#email-notifications') return undefined;
    const t = window.setTimeout(() => {
      document.getElementById('email-notifications')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 150);
    return () => window.clearTimeout(t);
  }, []);

  const handleToggleSubscription = async () => {
    setSubLoading(true);
    setSubSuccess('');
    setSubError('');
    try {
      const newVal = !emailSubscribed;
      await updateEmailSubscription(newVal);
      setEmailSubscribed(newVal);
      setUser((prev) => (prev ? { ...prev, emailSubscribed: newVal } : prev));
      setSubSuccess(newVal ? 'Subscribed to daily picks emails!' : 'Unsubscribed from daily picks emails.');
    } catch (err) {
      setSubError(err.response?.data?.error || 'Failed to update subscription');
    } finally {
      setSubLoading(false);
    }
  };

  const handleToggleResultsSubscription = async () => {
    setResSubLoading(true);
    setResSubSuccess('');
    setResSubError('');
    try {
      const newVal = !emailResultsSubscribed;
      await updateEmailResultsSubscription(newVal);
      setEmailResultsSubscribed(newVal);
      setUser((prev) => (prev ? { ...prev, emailResultsSubscribed: newVal } : prev));
      setResSubSuccess(newVal ? 'Subscribed to results emails!' : 'Unsubscribed from results emails.');
    } catch (err) {
      setResSubError(err.response?.data?.error || 'Failed to update subscription');
    } finally {
      setResSubLoading(false);
    }
  };

  const handleSportsbookChange = async (sportsbook) => {
    setSbLoading(true);
    setSbSuccess('');
    setSbError('');
    try {
      await updateUserSportsbook(sportsbook);
      setUser((prev) => (prev ? { ...prev, preferredSportsbook: sportsbook } : prev));
      setSbSuccess('Preferred sportsbook saved. It will be used by default on prediction pages.');
    } catch (err) {
      setSbError(err.response?.data?.error || 'Failed to save sportsbook preference');
    } finally {
      setSbLoading(false);
    }
  };

  if (!user) return <Container><Title>Profile</Title><p>Loading...</p></Container>;

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setSuccess('');
    setError('');
    try {
      const res = await axios.put('/user', form);
      setUser(res.data);
      setSuccess('Profile updated!');
    } catch (err) {
      setError(err.response?.data?.error || 'Update failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePwChange = e => {
    setPwForm({ ...pwForm, [e.target.name]: e.target.value });
  };

  const handlePwSubmit = async e => {
    e.preventDefault();
    setPwLoading(true);
    setPwSuccess('');
    setPwError('');
    if (pwForm.newPassword !== pwForm.confirmNewPassword) {
      setPwError('New passwords do not match');
      setPwLoading(false);
      return;
    }
    try {
      await axios.put('/user/password', {
        currentPassword: pwForm.currentPassword,
        newPassword: pwForm.newPassword
      });
      setPwSuccess('Password updated successfully!');
      setPwForm({ currentPassword: '', newPassword: '', confirmNewPassword: '' });
    } catch (err) {
      setPwError(err.response?.data?.error || 'Password update failed');
    } finally {
      setPwLoading(false);
    }
  };

  return (
    <Container theme={theme}>
      <Title theme={theme}>User Profile</Title>
      <form onSubmit={handleSubmit}>
        <Field>
          <Label theme={theme}>Email</Label>
          <Input theme={theme} type="email" value={user.email} disabled />
        </Field>
        <Field>
          <Label theme={theme}>First Name</Label>
          <Input theme={theme} name="firstName" value={form.firstName} onChange={handleChange} />
        </Field>
        <Field>
          <Label theme={theme}>Last Name</Label>
          <Input theme={theme} name="lastName" value={form.lastName} onChange={handleChange} />
        </Field>
        <Button type="submit" disabled={loading}>{loading ? 'Saving...' : 'Save Changes'}</Button>
        {success && <Success theme={theme}>{success}</Success>}
        {error && <Error theme={theme}>{error}</Error>}
      </form>
      <PasswordSection theme={theme}>
        <Title theme={theme} style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>Change Password</Title>
        <form onSubmit={handlePwSubmit}>
          <Field>
            <Label theme={theme}>Current Password</Label>
            <Input
              theme={theme}
              type="password"
              name="currentPassword"
              value={pwForm.currentPassword}
              onChange={handlePwChange}
              required
            />
          </Field>
          <Field>
            <Label theme={theme}>New Password</Label>
            <Input
              theme={theme}
              type="password"
              name="newPassword"
              value={pwForm.newPassword}
              onChange={handlePwChange}
              required
            />
          </Field>
          <Field>
            <Label theme={theme}>Confirm New Password</Label>
            <Input
              theme={theme}
              type="password"
              name="confirmNewPassword"
              value={pwForm.confirmNewPassword}
              onChange={handlePwChange}
              required
            />
          </Field>
          <Button type="submit" disabled={pwLoading}>{pwLoading ? 'Saving...' : 'Change Password'}</Button>
          {pwSuccess && <Success theme={theme}>{pwSuccess}</Success>}
          {pwError && <Error theme={theme}>{pwError}</Error>}
        </form>
      </PasswordSection>

      <SportsbookSection theme={theme}>
        <Title theme={theme} style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>Odds / Sportsbook</Title>
        <Field>
          <Label theme={theme}>Preferred sportsbook for odds</Label>
          <SportsbookRow>
            <SportsbookDropdown
              options={sportsbooks}
              value={preferredSportsbook}
              onChange={handleSportsbookChange}
              ariaLabel="Preferred sportsbook"
            />
            {sbLoading && <span style={{ color: theme?.secondaryText }}>Saving...</span>}
          </SportsbookRow>
          <p style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: theme?.secondaryText }}>
            This choice is used by default on Total Bases, Hit, 2+ Hits, and Home Run prediction pages.
          </p>
          {sbSuccess && <Success theme={theme}>{sbSuccess}</Success>}
          {sbError && <Error theme={theme}>{sbError}</Error>}
        </Field>
      </SportsbookSection>

      <NotificationsSection id="email-notifications" theme={theme}>
        <Title theme={theme} style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>Email Notifications</Title>
        <Field>
          <Label theme={theme}>Daily Picks & Parlay emails</Label>
          <ToggleRow>
            <ToggleSwitch
              type="button"
              theme={theme}
              $active={emailSubscribed}
              onClick={handleToggleSubscription}
              disabled={subLoading}
              aria-pressed={emailSubscribed}
              aria-label="Toggle daily picks email subscription"
            />
            <ToggleLabel theme={theme}>
              {emailSubscribed ? <FaBell size={14} /> : <FaBellSlash size={14} />}
              {emailSubscribed ? 'Subscribed' : 'Not subscribed'}
            </ToggleLabel>
            {subLoading && <span style={{ color: theme?.secondaryText, fontSize: '0.85rem' }}>Saving...</span>}
          </ToggleRow>
          <p style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: theme?.secondaryText }}>
            Receive emails for new Picks of the Day and Parlay of the Day as they're generated throughout the day.
          </p>
          {subSuccess && <Success theme={theme}>{subSuccess}</Success>}
          {subError && <Error theme={theme}>{subError}</Error>}
        </Field>
        <Field style={{ marginTop: '1.25rem' }}>
          <Label theme={theme}>Results & Winners emails</Label>
          <ToggleRow>
            <ToggleSwitch
              type="button"
              theme={theme}
              $active={emailResultsSubscribed}
              onClick={handleToggleResultsSubscription}
              disabled={resSubLoading}
              aria-pressed={emailResultsSubscribed}
              aria-label="Toggle results email subscription"
            />
            <ToggleLabel theme={theme}>
              {emailResultsSubscribed ? <FaBell size={14} /> : <FaBellSlash size={14} />}
              {emailResultsSubscribed ? 'Subscribed' : 'Not subscribed'}
            </ToggleLabel>
            {resSubLoading && <span style={{ color: theme?.secondaryText, fontSize: '0.85rem' }}>Saving...</span>}
          </ToggleRow>
          <p style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: theme?.secondaryText }}>
            Receive emails when games are final with results — find out if the Picks and Parlays of the Day were winners.
          </p>
          {resSubSuccess && <Success theme={theme}>{resSubSuccess}</Success>}
          {resSubError && <Error theme={theme}>{resSubError}</Error>}
        </Field>
      </NotificationsSection>
    </Container>
  );
};

export default ProfilePage; 